import { useEffect } from "react"

import { useNeoletterUserProfileQuery } from "shared/api/neoletter-user-profile/queries.generated"
import { useUpdateNeoletterUserProfile } from "shared/api/neoletter-user-profile/useUpdateNeoletterUserProfile"
import { Logger } from "shared/lib/Logger"
import { useAppSidebar } from "shared/lib/app-sidebar/app-sidebar-store"
import { exists } from "shared/lib/exists"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"

// Helper: Convert server string to local boolean
function parseServerValue(
  value: string | null | undefined
): boolean | undefined {
  if (value === "true") return true
  if (value === "false") return false
  return undefined
}

// Helper: Convert local boolean to server string
function toServerValue(value: boolean | undefined): string | undefined {
  if (value === undefined) return undefined
  return String(value)
}

export function useAppSidebarSync() {
  const { isAuthenticated } = useAuth()
  const { isExpanded, setIsExpanded } = useAppSidebar()
  const { updateProfile } = useUpdateNeoletterUserProfile()

  const { data } = useNeoletterUserProfileQuery({
    skip: !isAuthenticated
  })

  const updateSidebarExpanded = useEvent((value: boolean) => {
    setIsExpanded(value)

    if (isAuthenticated) {
      updateProfile({
        customFields: {
          sidebar_is_expanded: toServerValue(value)
        }
      }).catch(() => {
        Logger.error(
          "UpdateProfileError",
          "unable to persist sidebar isExpanded state"
        )
      })
    }
  })

  useEffect(() => {
    const serverValueString =
      data?.neoletterUserProfile?.custom_fields?.sidebar_is_expanded
    const serverValue = parseServerValue(serverValueString)

    if (exists(serverValue) && serverValue !== isExpanded) {
      setIsExpanded(serverValue)
    }
  }, [data])

  return {
    persistIsExpanded: updateSidebarExpanded
  }
}
